import { lazy, type ComponentType } from "react";
import { clearReloadAttempt } from "@/lib/chunkReload";

/**
 * Drop-in replacement for `React.lazy` that:
 *  1. Retries the dynamic import once after a short delay — recovers *transient*
 *     fetch blips (a flaky network moment), where the browser evicts the failed
 *     module-map entry so a second attempt can succeed.
 *  2. Clears the stale-chunk reload guard on a *successful* load — a chunk that
 *     actually loaded is the definitive "we recovered" signal, so a later,
 *     distinct stale chunk is free to auto-reload again. A genuinely-broken
 *     deploy never resolves here, so the guard stays set and we don't loop
 *     (see lib/chunkReload.ts).
 *
 * A genuine post-deploy hash-miss (the chunk no longer exists) re-fails both
 * attempts by design — that is recovered by the one-time reload path, not here.
 * We do NOT append a cache-busting query to the specifier: Vite statically
 * rewrites code-split `import()` specifiers at build time, so a runtime `?v=…`
 * would resolve to a non-existent asset.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  { retryDelayMs = 400 }: { retryDelayMs?: number } = {},
): React.LazyExoticComponent<T> {
  return lazy(withRetry(factory, { retryDelayMs }));
}

/**
 * The loader half of `lazyWithRetry`, for a lazy component that must be
 * created per key inside `useMemo` (a docs article or help topic body): there
 * React's own `lazy` has to be the call the hooks lint recognises, so it wraps
 * this. Same retry, same reload-guard clearing.
 */
export function withRetry<M>(
  factory: () => Promise<M>,
  { retryDelayMs = 400 }: { retryDelayMs?: number } = {},
): () => Promise<M> {
  return () =>
    factory()
      .catch(
        () =>
          new Promise<M>((resolve, reject) => {
            setTimeout(() => factory().then(resolve, reject), retryDelayMs);
          }),
      )
      .then((mod) => {
        clearReloadAttempt();
        return mod;
      });
}
